/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useState } from 'react';
import { useMutation } from 'react-query';

import { QUERIES_EXTERNAL, QUERIES_MISCELLANEOUS } from '@constants';

import { Grid, Loading } from 'components/atoms';
import { onErrorMessage } from 'helpers';
import { createMiscellaneousAddress, getAddressExternalApi } from 'services';

import { InputMask, InputText } from 'components/molecules';
import { OwnerDataWrap } from './styles';
import type { IAPerson } from '@types';

import type { OwnerDataInterface } from './interface';

const OwnerData = ({ onChangeValues, values }: OwnerDataInterface) => {
    const titleMessagesAddress = 'Endereço';

    const useField = useCallback(
        (key: keyof IAPerson) => {
            let value = null;

            if (values !== undefined) {
                value = values?.[key];
            }

            return {
                onChange: (newValue: string) =>
                    onChangeValues({ ...values, [String(key)]: newValue }),
                value: value ? String(value) : '',
                id: String(key),
                name: String(key),
                key
            };
        },
        [values]
    );

    const [isValidatedAddress, setIsValidatedAddress] = useState(true);
    const [isManualNeighborhood, setIsManualNeighborhood] = useState(false);


    const { mutate: onCreateAddress, isLoading: isLoadingCreateAddress } = useMutation(
        (payload: {
            bairro: string;
            cidade: string;
            uf: string;
            logradouro: string;
            complemento: string;
        }) => createMiscellaneousAddress(payload),
        {
            mutationKey: QUERIES_MISCELLANEOUS.ADDRESS.query,
            onSuccess: (res) => {
                onChangeValues({ ...values, bairrosIds: res?.data?.bairrosIds });
            },
            onError: (err) => {
                onErrorMessage(titleMessagesAddress, err);
            },
        }
    );

    const { mutate: onGetAddress, isLoading: isLoadingGetAddress } = useMutation(
        () => getAddressExternalApi(values?.cep || ''),
        {
            mutationKey: QUERIES_EXTERNAL.ADDRESS.consult,
            onSuccess: (res) => {

                setIsValidatedAddress(true);

                const newAddress = {
                    bairros: {
                        nome: res.bairro,
                        cidade: {
                            nome: res.localidade,
                            uf: res.uf,
                        },
                    },
                    logradouro: res.logradouro,
                    complemento: res.complemento,
                };

                let requestData = res;

                if (isManualNeighborhood) {
                    requestData = {
                        ...requestData,
                        bairro: values?.bairro,
                    };
                } else {
                    setIsManualNeighborhood(res?.bairro === '');
                }


                onChangeValues({ ...values, ...newAddress });
                onCreateAddress(requestData);
            },
            onError: (err) => {
                onErrorMessage(titleMessagesAddress, err);
            },
        }
    );


    const onChangedCep = (newValue: string) => {
        if (isValidatedAddress) {
            setIsValidatedAddress(false);
        }

        onChangeValues({ ...values, cep: newValue });
    }



    const onBlurCep = () => {
        if (values?.cep?.length === 8 && !isValidatedAddress) {
            onGetAddress();
        }
    }

    const onBlurNeighborhood = () => {
        if (values?.bairro !== '') {
            onGetAddress();
        }
    }


    let localizationValue = `${values?.bairros?.nome || ''} - ${values?.bairros?.cidade?.nome || ''
        } - ${values?.bairros?.cidade?.uf || ''}`

    if (isManualNeighborhood) {
        localizationValue = `${values?.bairros?.cidade?.nome || ''
            } - ${values?.bairros?.cidade?.uf || ''}`
    }


    return (
        <OwnerDataWrap>

            <Loading active={isLoadingCreateAddress || isLoadingGetAddress} />

            <InputMask
                model="sinary"
                required
                mask="999.999.999-99"
                label="Documento do responsável(CPF)"
                placeholder="Informe o documento"
                {...useField('documento')}
            />

            <Grid numberColumns={2}>
                <InputText
                    model="sinary"
                    required
                    label="Nome do responsável"
                    placeholder="Informe o nome"
                    {...useField('nome')}

                />

                <InputText
                    model="sinary"
                    required
                    label="E-mail do responsável"
                    placeholder="Informe o email"
                    {...useField('email')}

                />
            </Grid>

            <Grid numberColumns={2}>

                <InputMask
                    model="sinary"
                    required
                    valueMasked
                    mask="99/99/9999"
                    label="Data de nascimento do responsável"
                    placeholder="Informe a data de nascimento"
                    {...useField('nascimento')}
                />

                <InputMask
                    model="sinary"
                    required
                    mask={
                        (values?.responsaveis?.telefone?.length || 9) > 10
                            ? '(99) 9 9999-9999'
                            : '(99) 9999-9999 9'
                    }
                    label="Telefone do responsável"
                    placeholder="Informe o telefone"
                    {...useField('telefone')}

                />

            </Grid>


            <Grid numberColumns={isManualNeighborhood ? 3 : 2}>
                <InputMask
                    model="sinary"
                    required
                    mask="99999-999"
                    label="Cep do responsável"
                    placeholder="Informe o cep"
                    {...useField('cep')}
                    onChange={onChangedCep}
                    onBlur={onBlurCep}
                />

                {isManualNeighborhood && (
                    <InputText
                        model="sinary"
                        required
                        label="Bairro do responsável"
                        placeholder="Informe o bairro"
                        {...useField('bairro')}
                        onBlur={onBlurNeighborhood}
                    />
                )}

                <InputText
                    model="sinary"
                    required
                    label={
                        isManualNeighborhood ? 'Cidade / UF do Responsável' : 'Bairro / Cidade / UF do Responsável'
                    }
                    name="localizacao"
                    placeholder="Preencha o cep primeiro"
                    value={localizationValue}
                    onChange={() => null}
                />
            </Grid>

            <Grid numberColumns={2}>
                <InputText
                    model="sinary"
                    required
                    label="Logradouro do responsável"
                    placeholder="Informe o logradouro"
                    {...useField('logradouro')}
                />

                <InputText
                    model="sinary"
                    required
                    label="Número do responsável"
                    placeholder="Informe o número"
                    {...useField('numero')}
                />
            </Grid>

            <Grid numberColumns={2}>
                <InputText
                    model="sinary"
                    label="Complemento do responsável"
                    placeholder="Informe o complemento"
                    {...useField('complemento')}
                />

                <InputText
                    model="sinary"
                    label="Referência do responsável"
                    {...useField('referencia')}
                    placeholder="Informe a referência"
                />
            </Grid>

        </OwnerDataWrap>
    );
};

export default OwnerData;
